import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TrackByService } from '@app/shared/services';
import { GenericService } from '@services/generic.service';
import { Select, Store } from '@ngxs/store';
import {
  FetchLCLManualQuotes,
  FetchLCLQuotes,
  FetchLCLShipments,
  LCLShippingsSelectors,
  ResetLCLData,
  UpdateLoadingState,
} from '@app/modules/lcl-shipping/state';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { LclQuoteCardModel } from '@data-layer/models/lcl/lcl-quote-card.model';
import { LclShipmentCardModel } from '@data-layer/models/lcl/lcl-shipment-card.model';
import { CompaniesService } from '@services/companies.service';
import { DEFAULT_MAX_LIMIT } from '@constants/constants';
import { take, takeUntil, tap } from 'rxjs/operators';
import { CompanyModel } from '@data-layer/models/companies';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { enumHeaderActionId } from '@app/modules/app-layout/enums';
import { IHeaderAction } from '@app/modules/app-layout/interfaces';
import { UtilsHelper } from '@helpers/utils-helper';
import { enumExternalQuoteState } from '@data-layer/enums/lcl';
import { Router } from '@angular/router';
import { QuoteManualDetailedModel } from '@data-layer/models/lcl';

@Component({
  selector: 'lcl-dashboard-placeholder',
  templateUrl: './lcl-dashboard-placeholder.component.html',
  styleUrls: ['./lcl-dashboard-placeholder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclDashboardPlaceholderComponent implements OnInit, OnDestroy {
  @Input() public isRedirectExternal: boolean;

  @Select(LCLShippingsSelectors.isLoading) public isLoading$: Observable<boolean>;
  @Select(LCLShippingsSelectors.getFilteredQuotesByExistingShipments) public quotes$: Observable<LclQuoteCardModel[]>;
  @Select(LCLShippingsSelectors.getLCLManualQuotes) public manualQuotes$: Observable<QuoteManualDetailedModel[]>;
  @Select(LCLShippingsSelectors.getLCLShipments) public shipments$: Observable<LclShipmentCardModel[]>;

  public trackByFn: (item: any) => string | number | string = TrackByService.trackById;
  public isLaptop: boolean;
  public isLoading: boolean;

  public allQuotes$: BehaviorSubject<LclQuoteCardModel[]> = new BehaviorSubject<LclQuoteCardModel[]>([]);

  private defaultOffset: number = 0;
  private defaultLimit: number = 2;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private store: Store,
    private genericService: GenericService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private appLayoutService: AppLayoutService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.genericService.isLaptop$.pipe(takeUntil(this.destroy$)).subscribe((isLaptop: boolean) => {
      this.isLaptop = isLaptop;

      this.cdr.markForCheck();
    });

    this.companiesService.companyChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: { previous: CompanyModel; current: CompanyModel }) => this.loadData(data?.current?.id));

    this.appLayoutService.headerActions$.pipe(take(1)).subscribe((data: IHeaderAction[]) => {
      data.push({ id: enumHeaderActionId.LCL, routerLink: ['lcl-shipping', 'new'], title: 'BUTTON.ADD_NEW_LCL' });
      this.appLayoutService.headerActions$.next(UtilsHelper.uniqBy<IHeaderAction>(data, 'title'));
    });

    if (this.isRedirectExternal) {
      this.quotes$.pipe(takeUntil(this.destroy$)).subscribe((items: LclQuoteCardModel[]) => {
        const externalCreatedItem: LclQuoteCardModel = items.find(
          (item: LclQuoteCardModel) => item.externalQuoteState === enumExternalQuoteState.EXTERNAL_CREATED,
        );
        if (!!externalCreatedItem) {
          this.router.navigate(['/lcl-shipping', externalCreatedItem.id, 'edit'], { queryParams: { isAccessedExternal: true } });
        }
      });
    }

    combineLatest([this.quotes$, this.manualQuotes$])
      .pipe(
        tap(([quotes, manualQuotes]: [LclQuoteCardModel[], QuoteManualDetailedModel[]]) => {
          this.allQuotes$.next(this.sortQuotes(quotes, manualQuotes)); // due to type mismatch we have to convert it
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.appLayoutService.headerActions$.pipe(take(1)).subscribe((data: IHeaderAction[]) => {
      this.appLayoutService.headerActions$.next(data.filter((i: IHeaderAction) => i.id !== enumHeaderActionId.LCL));
    });

    this.destroy$.next();
  }

  private loadData(id: number): void {
    this.store.dispatch(new ResetLCLData());

    this.store
      .dispatch([
        new FetchLCLQuotes({ companyId: id, offset: 0, limit: DEFAULT_MAX_LIMIT }),
        new FetchLCLManualQuotes({ companyId: id, offset: 0, limit: DEFAULT_MAX_LIMIT }),
        new FetchLCLShipments({ companyId: id, offset: this.defaultOffset, limit: this.defaultLimit }),
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(new UpdateLoadingState(false));
      });
  }

  private sortQuotes(quotes: LclQuoteCardModel[], manualQuotes: QuoteManualDetailedModel[]): LclQuoteCardModel[] {
    const manualQuotesMapped: QuoteManualDetailedModel[] = manualQuotes.map((item: QuoteManualDetailedModel) => ({
      ...item,
      isManualQuote: true,
    }));

    return [...quotes, ...(manualQuotesMapped as unknown as LclQuoteCardModel[])].sort(
      (a: LclQuoteCardModel, b: LclQuoteCardModel) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    );
  }
}
